import { useEffect, useState } from 'react';
import { usePodeVerDetalhe } from '../ServiceLevelContext';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import {
  HOJE, getPedidosParaExport, getRegioes, getSellerPedidos, getSellerResumo, SellerResumo,
} from '../api';
import { getPrefs } from '../auth';
import KpiCard from '../components/KpiCard';
import Dica from '../components/Dica';
import { AJUDA } from '../utils/ajudas';
import TrendChart from '../components/TrendChart';
import Pagination from '../components/Pagination';
import { PedidoRow, Problema, Regiao } from '../types';
import {
  downloadCsv, fmtDataHora, fmtDataHoraFoto, fmtDias, fmtMoney, fmtMoney2, fmtNum, fmtPct, fmtPp,
} from '../utils/format';

const PAGE_SIZE = 20;
const MES_INICIO = HOJE.slice(0, 8) + '01';

interface Filtros {
  problema: Problema | '';
  de: string;
  ate: string;
  regiao_id: string;
  busca: string;
}

function FlagBadges({ p }: { p: PedidoRow }) {
  return (
    <>
      {p.flag_p1 && <span className="badge p1">P1 · {fmtDias(p.horas_em_atraso)}</span>}{' '}
      {p.flag_p2 && <span className="badge p2">P2 · {fmtPct(p.pct_atendimento_qtd, 0)}</span>}{' '}
      {p.flag_p3 && <span className="badge p3">P3 · {fmtMoney2(p.valor_corte)}</span>}
    </>
  );
}

export default function SellerDetail() {
  const { id } = useParams();
  const sellerId = Number(id);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const industriaId = params.get('industria') ? Number(params.get('industria')) : null;
  const prefs = getPrefs();

  const [resumo, setResumo] = useState<SellerResumo | null>(null);
  const [erro, setErro] = useState('');
  const [regioes, setRegioes] = useState<Regiao[]>([]);

  const [filtros, setFiltros] = useState<Filtros>({ problema: '', de: MES_INICIO, ate: HOJE, regiao_id: '', busca: '' });
  const [page, setPage] = useState(1);
  const [pedidos, setPedidos] = useState<{ total: number; pedidos: PedidoRow[] }>({ total: 0, pedidos: [] });
  const [loadingPedidos, setLoadingPedidos] = useState(true);
  const [exportando, setExportando] = useState(false);
  const podeVerPedidos = usePodeVerDetalhe(3);

  useEffect(() => {
    setResumo(null);
    getSellerResumo(sellerId, prefs.janela_ref || 'YTD', { de: prefs.custom_de, ate: prefs.custom_ate }, industriaId)
      .then(setResumo)
      .catch((e: Error) => setErro(e.message));
    getRegioes().then(setRegioes).catch(() => {});
  }, [sellerId, industriaId]);

  useEffect(() => {
    if (!podeVerPedidos) { setLoadingPedidos(false); return; }
    setLoadingPedidos(true);
    getSellerPedidos(sellerId, { ...filtros, industria_id: industriaId, page, page_size: PAGE_SIZE })
      .then(setPedidos)
      .catch((e: Error) => setErro(e.message))
      .finally(() => setLoadingPedidos(false));
  }, [sellerId, industriaId, filtros, page, podeVerPedidos]);

  const setF = (patch: Partial<Filtros>) => { setFiltros((f) => ({ ...f, ...patch })); setPage(1); };
  const toggleProblema = (p: Problema) => setF({ problema: filtros.problema === p ? '' : p });

  // exportação respeitando os filtros ativos (SDD §7.2) — evidência para a conversa com o distribuidor
  const exportar = async () => {
    setExportando(true);
    try {
      const rows = await getPedidosParaExport(sellerId, { ...filtros, industria_id: industriaId });
      downloadCsv(
        `pedidos_${resumo?.seller.nome_fantasia?.replace(/\s+/g, '_') || sellerId}_${filtros.de}_${filtros.ate}.csv`,
        ['Pedido', 'Data', 'Lojista', 'Região', 'Indústrias', 'Status', 'P1', 'Dias em atraso', 'P2', '% atendimento', 'P3', 'Corte (R$)', 'Valor pedido (R$)', 'SLA (dias)'],
        rows.map((p) => [
          p.pedido_id, String(p.data_pedido).slice(0, 10), p.lojista, p.regiao,
          (p.industrias_nomes ?? []).join(', '), p.status_atual,
          p.flag_p1 ? 'sim' : '', p.horas_em_atraso !== null ? (p.horas_em_atraso / 24).toFixed(1) : '',
          p.flag_p2 ? 'sim' : '', p.pct_atendimento_qtd !== null ? (p.pct_atendimento_qtd * 100).toFixed(1) : '',
          p.flag_p3 ? 'sim' : '', p.valor_corte.toFixed(2), p.valor_pedido.toFixed(2),
          p.sla_configurado ? p.sla_horas / 24 : `${p.sla_horas / 24} (default)`,
        ])
      );
    } finally {
      setExportando(false);
    }
  };

  if (erro) return <div className="card"><p className="empty">{erro}</p><Link to="/service-level/dashboard">← Voltar ao fechamento</Link></div>;
  if (!resumo) return <p className="empty">Carregando seller…</p>;

  const k = resumo.kpis;
  const pr = resumo.problemas;

  return (
    <>
      <div className="page-header">
        <div>
          <button className="btn secondary mb-2" onClick={() => navigate(-1)}><ArrowLeft size={14} /> Voltar</button>
          <h1>{resumo.seller.nome_fantasia}</h1>
          <div className="sub">
            {resumo.seller.razao_social} · CNPJ {resumo.seller.cnpj} · {resumo.seller.uf_base}
          </div>
          {resumo.industria && (
            <div className="mt-2">
              <span className="seller-chip">
                Recorte: {resumo.industria.nome}
                <button onClick={() => setParams({})} title="Limpar recorte de indústria">×</button>
              </span>
            </div>
          )}
        </div>
        <span className="foto-badge">Última atualização {fmtDataHoraFoto(resumo.ts_foto)}</span>
      </div>

      <div className="kpi-row">
        <KpiCard
          label="NS Quantidade (Fill Rate)" ajuda={AJUDA.ns}
          valores={{ hoje: k.hoje.ns_qtd, mtd: k.mtd.ns_qtd, m1: k.m1.ns_qtd, ref: k.ref.ns_qtd }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
          extra={`sobre pedidos já apurados (${fmtPct(k.mtd.pct_apurado)} do volume do mês)`}
        />
        <KpiCard
          label="Pedidos no Prazo" ajuda={AJUDA.prazo}
          valores={{ hoje: k.hoje.pct_no_prazo, mtd: k.mtd.pct_no_prazo, m1: k.m1.pct_no_prazo, ref: k.ref.pct_no_prazo }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
        />
        <KpiCard
          label="Corte" ajuda={AJUDA.corte}
          valores={{ hoje: k.hoje.pct_corte, mtd: k.mtd.pct_corte, m1: k.m1.pct_corte, ref: k.ref.pct_corte }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="down"
          extra={`${fmtMoney(k.mtd.corte_valor)} cortados no mês`}
        />
        <KpiCard
          label="Apuração do mês" ajuda={AJUDA.apuracao}
          valores={{ hoje: k.hoje.pct_apurado ?? null, mtd: k.mtd.pct_apurado ?? null, m1: k.m1.pct_apurado ?? null, ref: k.ref.pct_apurado ?? null }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
          extra={`${fmtNum(k.mtd.pedidos_em_aberto)} pedidos em aberto, sem nada faturado ainda`}
        />
      </div>

      <div className="card">
        <h2 className="flex items-start justify-between gap-2"><span>NS de quantidade — tendência diária do mês</span><Dica titulo="Tendência diária" texto={AJUDA.tendencia} /></h2>
        <TrendChart serie={resumo.serie_ns} />
      </div>

      {/* decomposição por problema — clicável, filtra a tabela abaixo (SDD §7.2) */}
      <div className="problema-row">
        <div className={`problema ${filtros.problema === 'P1' ? 'on' : ''}`} onClick={() => toggleProblema('P1')}>
          <div className="flex items-start justify-between"><span className="tag p1">P1</span><Dica titulo="P1 · Pedido parado/atrasado" texto={AJUDA.problemas} /></div>
          <h3>Pedidos parados / em atraso</h3>
          <div className="big">{fmtNum(pr.p1.pedidos)}</div>
          <div className="sub">no mês · {fmtNum(pr.p1.hoje)} enviados com atraso hoje</div>
        </div>
        <div className={`problema ${filtros.problema === 'P2' ? 'on' : ''}`} onClick={() => toggleProblema('P2')}>
          <div className="flex items-start justify-between"><span className="tag p2">P2</span><Dica titulo="P2 · Entrega parcial" texto={AJUDA.problemas} /></div>
          <h3>Entrega parcial</h3>
          <div className="big">{fmtNum(pr.p2.pedidos)}</div>
          <div className="sub">no mês · {fmtNum(pr.p2.hoje)} finalizados hoje</div>
        </div>
        <div className={`problema ${filtros.problema === 'P3' ? 'on' : ''}`} onClick={() => toggleProblema('P3')}>
          <div className="flex items-start justify-between"><span className="tag p3">P3</span><Dica titulo="P3 · Corte" texto={AJUDA.problemas} /></div>
          <h3>Corte (divergência de valor)</h3>
          <div className="big">{fmtMoney(pr.p3.valor)}</div>
          <div className="sub">{fmtNum(pr.p3.pedidos)} pedidos no mês · {fmtNum(pr.p3.hoje)} faturados com corte hoje</div>
        </div>
      </div>

      {podeVerPedidos && (
      <div className="card">
        <div className="page-header mb-3">
          <h2 className="m-0">Pedidos flagados</h2>
          <div className="filters">
            <select value={filtros.problema} onChange={(e) => setF({ problema: e.target.value as Problema | '' })}>
              <option value="">Todos os problemas</option>
              <option value="P1">P1 · Parado</option>
              <option value="P2">P2 · Entrega parcial</option>
              <option value="P3">P3 · Corte</option>
            </select>
            <select value={filtros.regiao_id} onChange={(e) => setF({ regiao_id: e.target.value })}>
              <option value="">Todas as regiões</option>
              {regioes.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
            <input type="date" value={filtros.de} onChange={(e) => setF({ de: e.target.value })} />
            <input type="date" value={filtros.ate} onChange={(e) => setF({ ate: e.target.value })} />
            <input placeholder="Nº do pedido ou lojista…" value={filtros.busca} onChange={(e) => setF({ busca: e.target.value })} />
            <button className="btn" onClick={exportar} disabled={exportando || pedidos.total === 0}>
              <Download size={14} /> {exportando ? 'Exportando…' : `Exportar (${fmtNum(pedidos.total)})`}
            </button>
          </div>
        </div>

        {loadingPedidos && <p className="empty">Carregando pedidos…</p>}

        {!loadingPedidos && (
          <>
            <div className="desktop-only">
              <table>
                <thead>
                  <tr>
                    <th>Pedido</th><th>Data</th><th>Lojista</th><th>Região</th><th>Status</th>
                    <th>Problemas</th><th className="num">Valor</th><th className="num">SLA</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidos.pedidos.map((p) => (
                    <tr key={p.pedido_id} className="clickable" onClick={() => navigate(`/service-level/pedidos/${p.pedido_id}`)}>
                      <td><strong>{p.pedido_id}</strong></td>
                      <td>{fmtDataHora(p.data_pedido)}</td>
                      <td>{p.lojista}</td>
                      <td>{p.regiao}</td>
                      <td><span className={`badge ${p.status_atual}`}>{p.status_atual.replace('_', ' ')}</span></td>
                      <td><FlagBadges p={p} /></td>
                      <td className="num">{fmtMoney2(p.valor_pedido)}</td>
                      <td className="num">
                        {p.sla_configurado
                          ? `${p.sla_horas / 24}d`
                          : <span className="badge default-sla" title="SLA não configurado — usando default global">{p.sla_horas / 24}d default</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pedidos.total === 0 && <p className="empty">Nenhum pedido flagado com esses filtros.</p>}
            </div>

            {/* mobile: cards com tap para o detalhe (SDD §7.7) */}
            <div className="mobile-only">
              {pedidos.pedidos.map((p) => (
                <Link key={p.pedido_id} to={`/service-level/pedidos/${p.pedido_id}`} className="pedido-card">
                  <div className="top">
                    <strong>{p.pedido_id}</strong>
                    <span className={`badge ${p.status_atual}`}>{p.status_atual.replace('_', ' ')}</span>
                  </div>
                  <div className="mid">{p.lojista} · {p.regiao} · {fmtDataHora(p.data_pedido)}</div>
                  <div className="bot">
                    <FlagBadges p={p} />
                    <span className="ml-auto font-bold">{fmtMoney2(p.valor_pedido)}</span>
                  </div>
                </Link>
              ))}
              {pedidos.total === 0 && <p className="empty">Nenhum pedido flagado com esses filtros.</p>}
            </div>

            <Pagination page={page} pageSize={PAGE_SIZE} total={pedidos.total} onChange={setPage} />
          </>
        )}
      </div>
      )}
    </>
  );
}
